import { Fragment, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { IconArrowNarrowRight, IconX } from '@rossum/ui/icons/tabler';
import {
  Autocomplete,
  Divider,
  IconButton,
  Paper,
  Stack,
  SvgIcon,
  TextField,
  Typography,
} from '@rossum/ui/material';
import {
  AUTO_DETECT,
  getLanguageLabel,
  getLanguageOptions,
  getTranslateLanguage,
  TranslateLanguage,
  TranslateSourceLanguage,
} from './languages';

type LanguageOption = {
  value: string;
  label: string;
};

type LanguageSelectProps = {
  options: LanguageOption[];
  value: LanguageOption;
  ariaLabel: string;
  dataCy: string;
  pinnedValue?: string;
  onChange: (value: string) => void;
};

const LanguageSelect = ({
  options,
  value,
  ariaLabel,
  dataCy,
  pinnedValue,
  onChange,
}: LanguageSelectProps) => (
  <Autocomplete
    size="small"
    autoHighlight
    openOnFocus
    selectOnFocus
    disableClearable
    options={options}
    value={value}
    getOptionLabel={option => option.label}
    isOptionEqualToValue={(option, selected) => option.value === selected.value}
    onChange={(_event, option) => onChange(option.value)}
    renderOption={({ key, ...optionProps }, option, { index }) => (
      <Fragment key={key}>
        <li {...optionProps}>{option.label}</li>
        {index === 0 && option.value === pinnedValue ? (
          <Divider component="li" role="presentation" />
        ) : null}
      </Fragment>
    )}
    slotProps={{ listbox: { sx: { maxHeight: 320 } } }}
    sx={{ minWidth: 200 }}
    renderInput={({
      inputProps: autocompleteHtmlInputProps,
      InputProps: autocompleteInputProps,
      ...params
    }) => (
      <TextField
        {...params}
        size="small"
        slotProps={{
          htmlInput: {
            ...autocompleteHtmlInputProps,
            'aria-label': ariaLabel,
            'data-cy': dataCy,
          },
          input: {
            ...autocompleteInputProps,
            sx: { fontSize: t => t.typography.pxToRem(14) },
          },
        }}
      />
    )}
  />
);

type TranslationPanelProps = {
  sourceLanguage: TranslateSourceLanguage;
  targetLanguage: TranslateLanguage;
  detectedSourceLanguage: TranslateLanguage | undefined;
  profileLanguage: TranslateLanguage | undefined;
  onSourceLanguageChange: (language: TranslateSourceLanguage) => void;
  onTargetLanguageChange: (language: TranslateLanguage) => void;
  onClose: () => void;
};

export const TranslationPanel = ({
  sourceLanguage,
  targetLanguage,
  detectedSourceLanguage,
  profileLanguage,
  onSourceLanguageChange,
  onTargetLanguageChange,
  onClose,
}: TranslationPanelProps) => {
  const intl = useIntl();

  const languageOptions = useMemo(
    () => getLanguageOptions(intl.locale),
    [intl.locale]
  );

  const autoDetectLabel = intl.formatMessage({
    id: 'components.documentValidation.translation.panel.sourceLanguage.auto',
  });

  const sourceOptions = useMemo(
    () => [{ value: AUTO_DETECT, label: autoDetectLabel }, ...languageOptions],
    [autoDetectLabel, languageOptions]
  );

  const decorateProfileLabel = useCallback(
    (label: string) =>
      intl.formatMessage(
        {
          id: 'components.documentValidation.translation.panel.targetLanguage.profile',
        },
        { language: label }
      ),
    [intl]
  );

  const targetOptions = useMemo(() => {
    const profileOption = languageOptions.find(
      option => option.value === profileLanguage
    );

    if (!profileOption) return languageOptions;

    return [
      { ...profileOption, label: decorateProfileLabel(profileOption.label) },
      ...languageOptions.filter(option => option.value !== profileLanguage),
    ];
  }, [languageOptions, profileLanguage, decorateProfileLabel]);

  const sourceValue = useMemo(() => {
    const getLabel = () => {
      if (sourceLanguage !== AUTO_DETECT) {
        return getLanguageLabel(sourceLanguage, intl.locale);
      }

      if (detectedSourceLanguage) {
        return intl.formatMessage(
          {
            id: 'components.documentValidation.translation.panel.sourceLanguage.detected',
          },
          { language: getLanguageLabel(detectedSourceLanguage, intl.locale) }
        );
      }

      return autoDetectLabel;
    };

    return { value: sourceLanguage, label: getLabel() };
  }, [sourceLanguage, detectedSourceLanguage, autoDetectLabel, intl]);

  const targetValue = useMemo(() => {
    const label = getLanguageLabel(targetLanguage, intl.locale);

    return {
      value: targetLanguage,
      label:
        targetLanguage === profileLanguage
          ? decorateProfileLabel(label)
          : label,
    };
  }, [targetLanguage, profileLanguage, decorateProfileLabel, intl.locale]);

  const handleSourceLanguageChange = (value: string) => {
    if (value === AUTO_DETECT) {
      onSourceLanguageChange(AUTO_DETECT);
      return;
    }

    const language = getTranslateLanguage(value);

    if (language) onSourceLanguageChange(language);
  };

  const handleTargetLanguageChange = (value: string) => {
    const language = getTranslateLanguage(value);

    if (language) onTargetLanguageChange(language);
  };

  return (
    <Stack
      component={Paper}
      elevation={4}
      sx={{ px: 2, py: 1, gap: 0.5 }}
      alignItems="center"
      direction="row"
      data-cy="translation-panel"
    >
      <Typography variant="body2" sx={{ mr: 2 }}>
        {intl.formatMessage({
          id: 'components.documentValidation.translation.panel.label',
        })}
      </Typography>

      <LanguageSelect
        options={sourceOptions}
        value={sourceValue}
        ariaLabel={intl.formatMessage({
          id: 'components.documentValidation.translation.panel.sourceLanguage.ariaLabel',
        })}
        dataCy="translation-panel-source-language"
        pinnedValue={AUTO_DETECT}
        onChange={handleSourceLanguageChange}
      />

      <SvgIcon color="action">
        <IconArrowNarrowRight />
      </SvgIcon>

      <LanguageSelect
        options={targetOptions}
        value={targetValue}
        ariaLabel={intl.formatMessage({
          id: 'components.documentValidation.translation.panel.targetLanguage.ariaLabel',
        })}
        dataCy="translation-panel-target-language"
        pinnedValue={profileLanguage}
        onChange={handleTargetLanguageChange}
      />

      <IconButton
        size="small"
        onClick={onClose}
        title={intl.formatMessage({
          id: 'components.documentValidation.translation.panel.close',
        })}
        sx={{ ml: 3 }}
      >
        <SvgIcon>
          <IconX />
        </SvgIcon>
      </IconButton>
    </Stack>
  );
};
